import React from 'react';
import './App.css';
import logo from './PlainLogo.png';
import founderPhoto from './Todd.jpg';
import dotMascot from './dot-assets/dot-mascot.png';
import dotFood from './dot-assets/food.png';
import dotDrink from './dot-assets/drink.png';
import dotBathroom from './dot-assets/bathroom.png';
import dotHurt from './dot-assets/hurt.png';
import dotFeelings from './dot-assets/feelings.png';
import dotPlaces from './dot-assets/places.png';

const WordCard = ({ emoji, word, tone }) => (
  <div className={`word-card ${tone}`}><span aria-hidden="true">{emoji}</span><strong>{word}</strong></div>
);

function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="JewelTek home"><img src={logo} alt="" /><span>JewelTek</span></a>
        <nav aria-label="Main navigation">
          <a href="#how-it-helps">How it helps</a><a href="#our-story">Our story</a><a className="nav-cta" href="#contact">Stay connected</a>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Communication starts with being understood</p>
            <h1>Helping children find their way to <em>communicate.</em></h1>
            <p className="hero-intro">JewelTek began with one simple need: helping my nonverbal autistic son express what he wanted, felt, and needed. Now I’m building tools that can support other children and families, too.</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#our-story">Why I built JewelTek <span>→</span></a>
              <a className="button button-secondary" href="#contact">Follow our progress</a>
            </div>
            <p className="founder-note"><span>♥</span> Built by a father for his son—and for families like ours</p>
            <div className="hero-trust" aria-label="JewelTek values">
              <span>Everyday words</span><span>Clear choices</span><span>Built from experience</span>
            </div>
          </div>

          <div className="communication-demo" aria-label="Example communication word cards">
            <div className="demo-glow" />
            <div className="demo-panel">
              <div className="demo-topline"><span>My words</span><span className="status-dot">● Ready to talk</span></div>
              <div className="word-grid">
                <WordCard emoji="🥤" word="Drink" tone="blue" /><WordCard emoji="🍎" word="Eat" tone="coral" />
                <WordCard emoji="▶" word="Play" tone="yellow" /><WordCard emoji="✋" word="Stop" tone="rose" />
                <WordCard emoji="🤝" word="Help" tone="purple" /><WordCard emoji="😊" word="Happy" tone="green" />
              </div>
              <div className="sentence-strip"><span>I want</span><strong>Drink</strong><button aria-label="Play sentence">▶</button></div>
            </div>
          </div>
        </section>

        <section className="promise" aria-label="Our promise">
          <p>What I set out to build</p><h2>A practical way to turn pictures<br />and words into communication.</h2>
          <div className="promise-grid">
            <article><span className="feature-icon blue-icon">●</span><h3>Communicate</h3><p>Help a child show what they need, what they feel, or what they want to say.</p></article>
            <article><span className="feature-icon yellow-icon">◆</span><h3>Learn</h3><p>Connect a picture to a word, hear it spoken, and practice using it in daily life.</p></article>
            <article><span className="feature-icon coral-icon">♥</span><h3>Connect</h3><p>Make it easier for parents, caregivers, and children to understand one another.</p></article>
          </div>
        </section>

        <section className="story" id="our-story">
          <div className="story-photo"><img src={founderPhoto} alt="Todd, founder of JewelTek, holding his son" /><div className="photo-caption"><span>“</span><p>I started with one goal:<br /><strong>help my son be understood.</strong></p></div></div>
          <div className="story-copy">
            <p className="eyebrow">Why I started JewelTek</p><h2>I built it for my son.<br />Now I want to help others.</h2>
            <p>I’m Todd, a single father raising a wonderful son with nonverbal autism. I wanted him to have a dependable way to tell me what he needed and to take part in the world around him.</p>
            <p>I started building a communication app that pairs pictures with spoken words. The goal was simple: help my son understand what a word means and give him a way to use it when he needs it.</p>
            <p>Seeing what this could do for him made me think about other children and parents facing the same challenges. JewelTek is my way of taking what I learned as a father and turning it into something useful for more families.</p>
            <div className="signature"><span>Todd</span><small>Founder & Dad</small></div>
          </div>
        </section>

        <section className="how" id="how-it-helps">
          <div className="section-heading"><p className="eyebrow">How the approach works</p><h2>See the word. Hear it. Use it.</h2><p>The experience is built around a simple routine children can repeat at home, at school, or with a caregiver.</p></div>
          <div className="steps">
            <article><b>01</b><h3>See it</h3><p>A clear picture helps the child recognize the choice or idea.</p></article>
            <article><b>02</b><h3>Hear it</h3><p>The app speaks the word so the picture and sound can be learned together.</p></article>
            <article><b>03</b><h3>Use it</h3><p>Repeating the process helps the child use the word in everyday situations.</p></article>
          </div>
        </section>

        <section className="dot-future" id="dot">
          <div className="dot-showcase" aria-label="Preview of the upcoming DOT communication app">
            <div className="dot-halo" />
            <div className="dot-phone">
              <div className="dot-app-header"><div><img src={dotMascot} alt="" /><b>DOT</b></div><span>🔊 Speak</span><span>💬 Parent</span></div>
              <div className="dot-app-grid">
                <div className="peach"><img src={dotFood} alt="" /><b>Food</b></div>
                <div className="sky"><img src={dotDrink} alt="" /><b>Drink</b></div>
                <div className="aqua"><img src={dotBathroom} alt="" /><b>Bathroom</b></div>
                <div className="pink"><img src={dotHurt} alt="" /><b>Hurt</b></div>
                <div className="yellow"><img src={dotFeelings} alt="" /><b>Feelings</b></div>
                <div className="green"><img src={dotPlaces} alt="" /><b>Places</b></div>
              </div>
              <div className="dot-app-sentence"><small>DOT SAYS</small><p>Tap a picture to build your message</p><div><b>▶ Speak</b><span>⌫ Undo</span><span>× Clear</span></div></div>
            </div>
            <img className="dot-real-mascot" src={dotMascot} alt="DOT, JewelTek’s ladybug communication companion" />
          </div>
          <div className="dot-copy">
            <span className="release-pill">Future JewelTek release</span>
            <p className="eyebrow">The next JewelTek project</p>
            <h2>Meet DOT.</h2>
            <p>DOT is the next step in what I started for my son. It uses simple picture choices to help autistic children communicate everyday needs, put together a message, and hear their words spoken aloud.</p>
            <div className="dot-feature-list"><span>Picture-led choices</span><span>Speak or silent modes</span><span>Parent messaging</span><span>Food, feelings & places</span></div>
            <p className="dot-note"><span>✦</span> My goal is to keep DOT calm, friendly, and easy to use for both children and the people supporting them.</p>
          </div>
        </section>

        <section className="contact" id="contact">
          <div><p className="eyebrow">I’m still building</p><h2>Follow JewelTek as<span className="connected-word"> it grows.</span></h2></div>
          <div className="contact-copy"><p>If you’re a parent, educator, therapist, or someone who believes in this work, I’d be glad to hear from you.</p><a className="button button-light" href="mailto:JewellTek@gmail.com">Contact JewelTek <span>→</span></a></div>
        </section>
      </main>

      <footer>
        <a className="brand" href="#top"><img src={logo} alt="" /><span>JewelTek</span></a>
        <p>Communication tools built from a father’s experience.</p>
        <nav className="footer-nav" aria-label="Footer navigation"><a href="#our-story">Our story</a><a href="#dot">DOT</a><a href="mailto:JewellTek@gmail.com">Contact</a></nav>
        <p>© {new Date().getFullYear()} JewelTek</p>
      </footer>
    </div>
  );
}

export default App;
